import type { JSX } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Tabs } from '@artosera/ui';
import { formaterOctets } from '../../../../lib/format.js';
import {
  enregistrerDecisions,
  lireDecisions,
  lireRapprochement,
  memesDecisions,
  nombreChangements,
  rapprocherLot,
  type DecisionImage,
  type DecisionsImages,
  type Rapprochement,
} from '../../../../lib/images.js';
import { raisonReprise, type LotReprise } from '../../../../lib/reprise.js';
import { messageReprise } from '../messages.js';
import reprise from '../Reprise.module.css';
import { FichierADecider, type OptionOeuvre } from './FichierADecider.js';
import styles from './Images.module.css';
import { ListePaginee } from './ListePaginee.js';

type Onglet = 'ambigus' | 'orphelins' | 'rapproches' | 'ignores' | 'nonPris' | 'sansImage';

const cleRevue = (idGalerie: string, lotId: string): readonly string[] => [
  'console',
  'galerie',
  idGalerie,
  'reprise',
  'lot',
  lotId,
];

/**
 * La revue d'un rapprochement : les compteurs de l'API, puis six listes
 * paginées — les ambigus d'abord, car ils bloquent tout. Les décisions de la
 * galerie se préparent ici, fichier par fichier ; un seul geste les enregistre
 * (`PATCH` avec la révision du lot) et relance le rapprochement.
 */
export function RevueRapprochement({
  idGalerie,
  lot,
  peutAgir,
  onLot,
}: {
  idGalerie: string;
  lot: LotReprise;
  peutAgir: boolean;
  onLot: (lot: LotReprise) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const revue = useQuery({
    queryKey: [...cleRevue(idGalerie, lot.id), 'rapprochement', lot.revision],
    queryFn: () => lireRapprochement(idGalerie, lot.id),
  });
  const enregistrees = useQuery({
    queryKey: [...cleRevue(idGalerie, lot.id), 'decisions', lot.revision],
    queryFn: () => lireDecisions(idGalerie, lot.id),
  });

  if (revue.isError || enregistrees.isError)
    return (
      <p className={reprise.erreur} role="alert">
        {messageReprise(t, revue.error ?? enregistrees.error, 'images') ?? t('images.revue.erreur')}
      </p>
    );
  if (!revue.data || !enregistrees.data)
    return <p className={reprise.discret}>{t('reprise.chargement')}</p>;
  // Le contenu se remonte à chaque révision du lot : un brouillon ne survit pas à un changement.
  return (
    <RevueContenu
      key={lot.revision}
      idGalerie={idGalerie}
      lot={lot}
      revue={revue.data}
      enregistrees={enregistrees.data}
      peutAgir={peutAgir}
      onLot={onLot}
    />
  );
}

function RevueContenu({
  idGalerie,
  lot,
  revue,
  enregistrees,
  peutAgir,
  onLot,
}: {
  idGalerie: string;
  lot: LotReprise;
  revue: Rapprochement;
  enregistrees: DecisionsImages;
  peutAgir: boolean;
  onLot: (lot: LotReprise) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const client = useQueryClient();
  const compteurs = revue.compteurs;
  const [onglet, setOnglet] = useState<Onglet>(compteurs.ambigus > 0 ? 'ambigus' : 'rapproches');
  const [brouillon, setBrouillon] = useState<DecisionsImages | null>(null);
  const [message, setMessage] = useState<{ ton: 'erreur' | 'note'; texte: string } | null>(null);
  const courantes = brouillon ?? enregistrees;
  const modifie = !memesDecisions(courantes, enregistrees);

  const decider = useCallback(
    (fichierId: string, decision: DecisionImage | null): void => {
      setMessage(null);
      setBrouillon((precedent) => {
        const suite = { ...(precedent ?? enregistrees) };
        if (decision === null) delete suite[fichierId];
        else suite[fichierId] = decision;
        return suite;
      });
    },
    [enregistrees],
  );

  const oeuvres = useMemo(() => {
    const connues = new Map<string, string>();
    for (const ambigu of revue.ambigus)
      for (const candidat of ambigu.candidats)
        connues.set(candidat.oeuvreId, `${candidat.inventaire} · ${candidat.titre}`);
    for (const oeuvre of revue.oeuvresSansImage)
      connues.set(oeuvre.oeuvreId, `${oeuvre.inventaire} · ${oeuvre.titre}`);
    for (const rapproche of revue.rapproches)
      connues.set(rapproche.oeuvreId, `${rapproche.inventaire} · ${rapproche.titre}`);
    return connues;
  }, [revue]);
  const etiquette = useCallback((oeuvreId: string) => oeuvres.get(oeuvreId) ?? oeuvreId, [oeuvres]);
  const sansImage: OptionOeuvre[] = useMemo(
    () => revue.oeuvresSansImage.map((o) => ({ ...o, ordre: null })),
    [revue.oeuvresSansImage],
  );

  const restantsDansBrouillon = revue.ambigus.filter(
    (ambigu) => courantes[ambigu.fichierId] === undefined,
  ).length;

  const recharger = (): void => {
    void client.invalidateQueries({ queryKey: cleRevue(idGalerie, lot.id) });
    void client.invalidateQueries({
      queryKey: ['console', 'galerie', idGalerie, 'reprise', 'lots'],
    });
  };

  const appliquer = useMutation({
    mutationFn: async () => {
      const modifiee = await enregistrerDecisions(idGalerie, lot.id, {
        revision: lot.revision,
        decisions: courantes,
      });
      onLot(modifiee);
      // Les décisions sont rangées : si le rapprochement est refusé, le lot reste à rapprocher.
      try {
        return { lot: await rapprocherLot(idGalerie, lot.id), refus: null };
      } catch (cause) {
        return { lot: modifiee, refus: messageReprise(t, cause, 'images') };
      }
    },
    onSuccess: ({ lot: suite, refus }) => {
      setBrouillon(null);
      if (refus === null) setMessage(null);
      else
        setMessage({
          ton: 'erreur',
          texte: t('images.revue.decisionsRangees', { message: refus }),
        });
      onLot(suite);
      recharger();
    },
    onError: (cause) => {
      if (raisonReprise(cause) === 'lot_modifie') {
        setBrouillon(null);
        setMessage({ ton: 'note', texte: t('images.revue.rechargee') });
        recharger();
        return;
      }
      setMessage({ ton: 'erreur', texte: messageReprise(t, cause, 'images') ?? '' });
    },
  });

  const bloque = !peutAgir || appliquer.isPending;
  const decisionDe = (fichierId: string): DecisionImage | undefined => courantes[fichierId];

  const ambigus = (
    <div className={reprise.pile}>
      {compteurs.ambigus > 0 ? (
        <p className={reprise.avertissement} role="note">
          {t('images.revue.ambigusBloquent', { count: compteurs.ambigus })}
        </p>
      ) : null}
      <ListePaginee
        libelle={t('images.revue.onglets.ambigus')}
        liste={revue.ambigus}
        total={compteurs.ambigus}
        texte={(ambigu) => ambigu.chemin}
        vide={t('images.revue.aucunAmbigu')}
        rendu={(ambigu) => (
          <FichierADecider
            key={ambigu.fichierId}
            chemin={ambigu.chemin}
            motif={t(`images.revue.motifsAmbigus.${ambigu.motif}`)}
            options={ambigu.candidats}
            decision={decisionDe(ambigu.fichierId)}
            etiquette={etiquette}
            desactive={bloque}
            onDecider={(decision) => decider(ambigu.fichierId, decision)}
          />
        )}
      />
    </div>
  );

  const orphelins = (
    <ListePaginee
      libelle={t('images.revue.onglets.orphelins')}
      liste={revue.orphelins}
      total={compteurs.orphelins}
      texte={(orphelin) => orphelin.chemin}
      vide={t('images.revue.aucunOrphelin')}
      rendu={(orphelin) => (
        <FichierADecider
          key={orphelin.fichierId}
          chemin={orphelin.chemin}
          motif={t(`images.revue.motifsOrphelins.${orphelin.motif}`)}
          details={
            orphelin.cles.length > 0 ? (
              <p className={styles.motif}>
                {t('images.revue.clesLues', { cles: orphelin.cles.join(', ') })}
              </p>
            ) : null
          }
          options={[]}
          recherche={sansImage}
          decision={decisionDe(orphelin.fichierId)}
          etiquette={etiquette}
          desactive={bloque}
          onDecider={(decision) => decider(orphelin.fichierId, decision)}
        />
      )}
    />
  );

  const rapproches = (
    <ListePaginee
      libelle={t('images.revue.onglets.rapproches')}
      liste={revue.rapproches}
      total={compteurs.rapproches}
      texte={(ligne) => `${ligne.chemin} ${ligne.inventaire} ${ligne.titre}`}
      vide={t('images.revue.aucunRapproche')}
      enLignes
      rendu={(ligne) => (
        <li key={ligne.fichierId}>
          <span className={styles.chemin}>{ligne.chemin}</span>
          <span className={styles.ligneDroite}>
            {t('images.revue.rattacheA', {
              inventaire: ligne.inventaire,
              titre: ligne.titre,
              ordre: ligne.ordre,
            })}
            <span className={reprise.bloc}>{formaterOctets(ligne.octets)}</span>
          </span>
          {ligne.decide || decisionDe(ligne.fichierId) ? (
            <span className={styles.actionsFichier}>
              <Badge tone="neutral">{t('images.revue.decide')}</Badge>
              {decisionDe(ligne.fichierId) ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={bloque}
                  aria-label={t('images.decision.retirerDe', { chemin: ligne.chemin })}
                  onClick={() => decider(ligne.fichierId, null)}
                >
                  {t('images.decision.retirer')}
                </Button>
              ) : null}
            </span>
          ) : (
            <span />
          )}
        </li>
      )}
    />
  );

  const ignores = (
    <ListePaginee
      libelle={t('images.revue.onglets.ignores')}
      liste={revue.ignores}
      total={compteurs.ignores}
      texte={(ligne) => ligne.chemin}
      vide={t('images.revue.aucunIgnore')}
      enLignes
      rendu={(ligne) => (
        <li key={ligne.fichierId}>
          <span className={styles.chemin}>{ligne.chemin}</span>
          <span className={styles.ligneDroite}>
            {t(`images.revue.motifsIgnores.${ligne.motif}`)}
          </span>
          {ligne.motif === 'decision' && decisionDe(ligne.fichierId) ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={bloque}
              aria-label={t('images.decision.retirerDe', { chemin: ligne.chemin })}
              onClick={() => decider(ligne.fichierId, null)}
            >
              {t('images.decision.retirer')}
            </Button>
          ) : (
            <span />
          )}
        </li>
      )}
    />
  );

  const nonPris = (
    <div className={reprise.pile}>
      <p className={reprise.texte}>{t('images.revue.nonPrisTexte')}</p>
      <ListePaginee
        libelle={t('images.revue.onglets.nonPris')}
        liste={revue.nonPrisEnCharge}
        total={compteurs.nonPrisEnCharge}
        texte={(ligne) => `${ligne.chemin} ${ligne.mime}`}
        vide={t('images.revue.aucunNonPris')}
        enLignes
        rendu={(ligne) => (
          <li key={ligne.fichierId}>
            <span className={styles.chemin}>{ligne.chemin}</span>
            <span className={styles.ligneDroite}>{ligne.raison ?? ligne.mime}</span>
            <span />
          </li>
        )}
      />
    </div>
  );

  const sansImageListe = (
    <div className={reprise.pile}>
      <p className={reprise.texte}>{t('images.revue.sansImageTexte')}</p>
      <ListePaginee
        libelle={t('images.revue.onglets.sansImage')}
        liste={revue.oeuvresSansImage}
        total={compteurs.oeuvresSansImage}
        texte={(oeuvre) => `${oeuvre.inventaire} ${oeuvre.titre}`}
        vide={t('images.revue.aucuneSansImage')}
        enLignes
        rendu={(oeuvre) => (
          <li key={oeuvre.oeuvreId}>
            <span className={styles.chemin}>{oeuvre.inventaire}</span>
            <span className={styles.ligneDroite}>{oeuvre.titre}</span>
            <span />
          </li>
        )}
      />
    </div>
  );

  const changements = nombreChangements(courantes, enregistrees);

  return (
    <div className={reprise.pile}>
      <dl
        className={`${reprise.compteurs} ${styles.compteurs}`}
        aria-label={t('images.revue.compteurs')}
      >
        <div>
          <dt>{t('images.revue.c.images')}</dt>
          <dd>{compteurs.images}</dd>
        </div>
        <div>
          <dt>{t('images.revue.c.rapproches')}</dt>
          <dd>{compteurs.rapproches}</dd>
        </div>
        <div
          data-bloquant={compteurs.ambigus > 0 ? 'true' : undefined}
          data-resolu={compteurs.ambigus === 0 ? 'true' : undefined}
        >
          <dt>{t('images.revue.c.ambigus')}</dt>
          <dd>{compteurs.ambigus}</dd>
        </div>
        <div>
          <dt>{t('images.revue.c.orphelins')}</dt>
          <dd>{compteurs.orphelins}</dd>
        </div>
        <div>
          <dt>{t('images.revue.c.ignores')}</dt>
          <dd>{compteurs.ignores}</dd>
        </div>
        <div>
          <dt>{t('images.revue.c.nonPris')}</dt>
          <dd>{compteurs.nonPrisEnCharge}</dd>
        </div>
        {compteurs.aConvertir ? (
          <div>
            <dt>{t('images.revue.c.aConvertir')}</dt>
            <dd>{compteurs.aConvertir}</dd>
          </div>
        ) : null}
        <div>
          <dt>{t('images.revue.c.sansImage')}</dt>
          <dd>{compteurs.oeuvresSansImage}</dd>
        </div>
        <div>
          <dt>{t('images.revue.c.morceaux')}</dt>
          <dd>{compteurs.morceaux}</dd>
        </div>
      </dl>
      {compteurs.decisionsSansFichier ? (
        <p className={reprise.discret}>
          {t('images.revue.decisionsSansFichier', { count: compteurs.decisionsSansFichier })}
        </p>
      ) : null}

      {message ? (
        <p
          className={message.ton === 'erreur' ? reprise.erreur : reprise.avertissement}
          role={message.ton === 'erreur' ? 'alert' : 'status'}
        >
          {message.texte}
        </p>
      ) : null}

      <Tabs
        value={onglet}
        onValueChange={(valeur) => setOnglet(valeur as Onglet)}
        tabs={[
          {
            value: 'ambigus',
            label: t('images.revue.onglet', {
              nom: t('images.revue.onglets.ambigus'),
              count: compteurs.ambigus,
            }),
            content: ambigus,
          },
          {
            value: 'orphelins',
            label: t('images.revue.onglet', {
              nom: t('images.revue.onglets.orphelins'),
              count: compteurs.orphelins,
            }),
            content: orphelins,
          },
          {
            value: 'rapproches',
            label: t('images.revue.onglet', {
              nom: t('images.revue.onglets.rapproches'),
              count: compteurs.rapproches,
            }),
            content: rapproches,
          },
          {
            value: 'ignores',
            label: t('images.revue.onglet', {
              nom: t('images.revue.onglets.ignores'),
              count: compteurs.ignores,
            }),
            content: ignores,
          },
          {
            value: 'nonPris',
            label: t('images.revue.onglet', {
              nom: t('images.revue.onglets.nonPris'),
              count: compteurs.nonPrisEnCharge,
            }),
            content: nonPris,
          },
          {
            value: 'sansImage',
            label: t('images.revue.onglet', {
              nom: t('images.revue.onglets.sansImage'),
              count: compteurs.oeuvresSansImage,
            }),
            content: sansImageListe,
          },
        ]}
      />

      {peutAgir && modifie ? (
        <div className={styles.barre} role="region" aria-label={t('images.revue.barre.titre')}>
          <div>
            <p>
              <strong>{t('images.revue.barre.decisions', { count: changements })}</strong>
            </p>
            <p className={reprise.discret}>
              {restantsDansBrouillon > 0
                ? t('images.revue.barre.restants', { count: restantsDansBrouillon })
                : t('images.revue.barre.rapprocher')}
            </p>
          </div>
          <div className={reprise.actions}>
            <Button
              variant="ghost"
              disabled={appliquer.isPending}
              onClick={() => {
                setBrouillon(null);
                setMessage(null);
              }}
            >
              {t('images.revue.barre.annuler')}
            </Button>
            <Button loading={appliquer.isPending} onClick={() => appliquer.mutate()}>
              {t('images.revue.barre.appliquer')}
            </Button>
          </div>
        </div>
      ) : null}
      {!modifie && Object.keys(enregistrees).length > 0 ? (
        <p className={reprise.discret}>
          {t('images.revue.decisionsRangeesNombre', { count: Object.keys(enregistrees).length })}
        </p>
      ) : null}
    </div>
  );
}
